import React, { useState, useEffect, useMemo, useCallback } from 'react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import jameelFont from "../assets/fonts/jameelnoorinastaliqfont";
import html2canvas from 'html2canvas';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faPrint, faSearch, faFileExcel, faSyncAlt } from '@fortawesome/free-solid-svg-icons';
import JsBarcode from 'jsbarcode';
import { FixedSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import Fuse from 'fuse.js';
import '../styles/armsform.css';

const FormList = () => {
    const navigate = useNavigate();
    const [gdsrEntries, setGdsrEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [lastRefreshTime, setLastRefreshTime] = useState(null);

    // Calculate new entries count
    const newEntriesCount = useMemo(() => {
        return gdsrEntries.filter(entry => entry.isNew).length;
    }, [gdsrEntries]);

    // Fuse.js for fuzzy search
    const fuse = useMemo(() => new Fuse(gdsrEntries, {
        keys: ['sr_no', 'token_id', 'book_no', 'date', 'name_news', 'crime', 'thana'],
        threshold: 0.3,
        includeScore: true
    }), [gdsrEntries]);

    const fetchEntries = useCallback(async () => {
        try {
            const response = await axios.post('https://chaman.gob.pk/eac_backend/form.php', {
                action: 'get_records',
            });

            if (Array.isArray(response.data)) {
                const now = new Date();
                setLastRefreshTime(now);

                // Parse and attach timestamp, then sort by newest date+time
                const sortedEntries = response.data
                    .map(entry => {
                        // Try to parse the date reliably
                        const timestamp = new Date(entry.date);
                        return {
                            ...entry,
                            timestamp: isNaN(timestamp.getTime()) ? new Date(0) : timestamp
                        };
                    })
                    .sort((a, b) => b.timestamp - a.timestamp); // Newest first

                setGdsrEntries(prevEntries => {
                    const existingIds = new Set(prevEntries.map(item => item.token_id));

                    const newEntries = sortedEntries
                        .filter(entry => !existingIds.has(entry.token_id))
                        .map(entry => ({ ...entry, isNew: true }));

                    const existingEntries = prevEntries
                        .map(entry => ({ ...entry, isNew: false }));

                    return [...newEntries, ...existingEntries];
                });
            } else {
                setGdsrEntries([]);
                console.error('Expected array but got:', response.data);
            }
        } catch (err) {
            setError('Failed to fetch Form entries');
            console.error(err);
        } finally {
            setLoading(false);
            setIsRefreshing(false);
        }
    }, []);

    useEffect(() => {
        // Initial load with small delay to show loading state
        const timer = setTimeout(() => {
            fetchEntries();
        }, 5);

        return () => clearTimeout(timer);
    }, [fetchEntries]);

    const refreshData = () => {
        setIsRefreshing(true);
        fetchEntries();
    };

    const filteredEntries = useMemo(() => {
        if (!searchTerm) return gdsrEntries;
        return fuse.search(searchTerm).map(result => result.item);
    }, [gdsrEntries, searchTerm, fuse]);

    const handleDelete = async (token_id) => {
        if (window.confirm(`Are you sure you want to delete the entry with Token ID: ${token_id}?`)) {
            try {
                const response = await axios.post('https://chaman.gob.pk/eac_backend/form.php', {
                    action: 'delete',
                    token_id,
                });
                if (response.data.status === 'success') {
                    setGdsrEntries(prev => prev.filter(entry => entry.token_id !== token_id));
                } else {
                    throw new Error(response.data.message || 'Failed to delete');
                }
            } catch (error) {
                alert(`Error: ${error.message}`);
            }
        }
    };

    const handleEdit = (entry) => {
        navigate('/dashboard/edit-fir', { state: { entry } });
    };

    const handleUrduExport = async (entry) => {
        try {
            const pdf = new jsPDF('p', 'mm', 'legal');

            // Create a temporary div to render Urdu text
            const urduContent = document.createElement('div');

            // Set contentUrdu styles for both pages
            urduContent.style.fontFamily = 'Jameel Noori Nastaleeq, sans-serif';
            urduContent.style.fontSize = '14px';
            urduContent.style.color = 'black';
            urduContent.style.textAlign = 'right';
            urduContent.style.padding = '15px';
            urduContent.style.width = '650px';
            urduContent.style.margin = '10 auto';

            urduContent.innerHTML = `
                <div style="display: flex; font-size:12px; justify-content: space-between; align-items: center; direction: rtl; margin-top: 10px; margin-bottom: 05px;">
                    <div style="flex: 1; text-align: right;">سیریل نمبر: ${entry.sr_no}</div>
                    <div style="flex: 1; text-align: center;">
                        <span>فارم نمبر</span> 24-5 (1)
                    </div>
                    <div style="flex: 1; text-align: left;">بک نمبر: ${entry.book_no}</div>
                </div>

                <span style="font-size: 15px; font-weight: bold; text-align: center; display: block;">
                    ابتدائی اطلائی رپورٹ نسبت جرم قابل دست اندازی لیویز رپورٹ شدہ زیر دفعہ 154 مجموعہ ضابطہ فوجداری
                </span>
                <div style="font-size: 12px; display: flex; justify-content: space-between; width: 100%; direction: rtl; margin-bottom: 10px; margin-top: 05px;">
                    <div>نمبر: ${entry.no}</div>
                    <div>تھانہ: ${entry.thana}</div>
                    <div>ضلع: ${entry.district}</div>
                    <div>تاریخ و وقت وقوعہ: ${entry.date}, ${entry.time}</div>
                </div>
                <br>
                <table id="firtable" style="width: 100%; border-collapse: collapse; direction: rtl; font-size: 14px;">
                    <tr>
                        <td style="padding: 8px; text-align: right; border: 0.5px solid #000; width: 1.8in;">1. تاریخ و وقت رپورٹ</td>
                        <td style="padding: 8px; text-align: right; border: 0.5px solid #000;">${entry.r_date}, ${entry.r_time}</td>
                        <td style="padding: 8px; text-align: right; border: 0.5px solid #000; width: 1.8in;">7. تھانہ سے راونگی کی تاریخ و وقت</td>
                        <td style="padding: 8px; text-align: right; border: 0.5px solid #000;">${entry.d_date}, ${entry.d_time}</td>
                    </tr>
                    <tr>
                        <td style="padding: 8px; text-align: right; border: 0.5px solid #000; width: 1.8in;">2. نام و اطلاع دہندہ و مستغیث</td>
                        <td style="padding: 8px; text-align: right; border: 0.5px solid #000;">${entry.name_news}</td>
                        <td colspan="2" style="padding: 8px; text-align: right; border: 0.5px solid #000;"></td>
                    </tr>
                    <tr>
                        <td style="padding: 8px; text-align: right; border: 0.5px solid #000; width: 1.8in;">3. مختصر کیفیت جرم (معہ دفعہ) و مال اگر کچھ لیا گیا ہو</td>
                        <td style="padding: 8px; text-align: right; border: 0.5px solid #000;">${entry.crime}</td>
                        <td colspan="2" style="padding: 8px; text-align: right; border: 0.5px solid #000;"></td>
                    </tr>
                    <tr>
                        <td style="padding: 8px; text-align: right; border: 0.5px solid #000; width: 1.8in;">4. جائے و قوعہ و فاصلہ تھانہ سے اور سمت</td>
                        <td style="padding: 8px; text-align: right; border: 0.5px solid #000;">${entry.place}</td>
                        <td colspan="2" style="padding: 8px; text-align: right; border: 0.5px solid #000;"></td>
                    </tr>
                    <tr>
                        <td style="padding: 8px; text-align: right; border: 0.5px solid #000; width: 1.8in;">5. نام و سکونت ملزم</td>
                        <td style="padding: 8px; text-align: right; border: 0.5px solid #000;">${entry.criminal_loc}</td>
                        <td colspan="2" style="padding: 8px; text-align: right; border: 0.5px solid #000;"></td>
                    </tr>
                    <tr>
                        <td style="padding: 8px; text-align: right; border: 0.5px solid #000; width: 1.8in;">6. کاروائی متعلقہ تفتیش اگر اطلاع درج کرنے میں کچھ توقف ہوا ہو تو اس کی وجہ بیان کی جائے</td>
                        <td style="padding: 8px; text-align: right; border: 0.5px solid #000;">${entry.operation}</td>
                        <td colspan="2" style="padding: 8px; text-align: right; border: 0.5px solid #000;"></td>
                    </tr>
                </table>

                <br>
                <div style="font-size: 14px; display: flex; justify-content: space-between; width: 100%; direction: rtl; gap: 20px;">
                    <div>دستخط: ــــــــــــــــــــــــــــــــــــــــــ</div>
                    <div>عہدہ: ــــــــــــــــــــــــــــــــــــــــــ</div>
                </div>
                <br>
                <span style="direction: rtl; font-size: 12px; font-weight: bold; text-align: center; display: block;">
                    نوٹ اطلاع کے نیچے اطلاع دہندہ کا دستخط ، مہر یا نشان انگوٹھا ہونا چاہئے اور آفیسر تحریر کنندہ (ابتدائی اطلاع) کے دستخط بطور تصدیق ہونے چاہئے۔
                </span>
                <br>
                <div style="direction: rtl; font-size: 11px; text-align: justify;">ابتدائی اطلاع: ${entry.remarks}</div>
            `;

            document.body.appendChild(urduContent);

            const canvas = await html2canvas(urduContent, { scale: 2 });
            const imgData = canvas.toDataURL('image/png');

            const imgWidth = 187;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            const barcodeCanvas = document.createElement('canvas');
            JsBarcode(barcodeCanvas, entry.token_id, { format: 'CODE128', displayValue: false, fontSize: 10 });

            const barcodeDataUrl = barcodeCanvas.toDataURL('image/png');
            const barcodeWidth = 35;
            const barcodeHeight = 15;
            const centerX = (pdf.internal.pageSize.width - barcodeWidth) / 2;

            pdf.addImage(imgData, 'PNG', 15, 10, imgWidth, imgHeight);
            pdf.addImage(barcodeDataUrl, 'PNG', centerX, pdf.internal.pageSize.height - 30, barcodeWidth, barcodeHeight);

            pdf.save('fir_report.pdf');
        } catch (error) {
            console.error("Error exporting PDF: ", error);
        }
    };

    const exportToExcel = () => {
        const worksheet = XLSX.utils.json_to_sheet(gdsrEntries);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Form License Entries');
        XLSX.writeFile(workbook, 'Form.xlsx');
    };

    const Row = ({ index, style }) => {
        const entry = filteredEntries[index];
        if (!entry) return null;

        return (
            <div style={style} className="firtable-row" dir="rtl">
                {/* <div className="table-cell">{entry.sr_no}</div> */}
                <div className="table-cell">{entry.token_id}</div>
                {/* <div className="table-cell">{entry.book_no}</div> */}
                <div className="table-cell">{entry.date}</div>
                {/* <div className="table-cell">{entry.name_news}</div> */}
                <div className="table-cell">{entry.crime}</div>
                <div className="table-cell">{entry.thana}</div>
                <div className="table-cell actions btn-group">
                    <button onClick={() => handleUrduExport(entry)} title="Print" className="print-btn">
                        <FontAwesomeIcon icon={faPrint} />
                    </button>
                    <button onClick={() => handleDelete(entry.token_id)} title="Delete" className="delete-btn">
                        <FontAwesomeIcon icon={faTrash} />
                    </button>
                </div>
            </div>
        );
    };

    if (loading) return (
        <div className="loading-container">
            <div className="modern-loading">
                <div className="loading-wave">
                    <div className="loading-bar"></div>
                    <div className="loading-bar"></div>
                    <div className="loading-bar"></div>
                    <div className="loading-bar"></div>
                </div>
                <p className="loading-text">Loading ...</p>
            </div>
        </div>
    );

    if (error) return (
        <div className="error-container">
            <p>Error: {error}</p>
            <button onClick={() => window.location.reload()}>Retry</button>
        </div>
    );

    return (
        <div className="certificate-dashboard">
          <div className="dashboard-header">
                          <div className="header-left">
                              {/* <h2>Local Certificate Entries</h2> */}
                              <span className="entries-count">Total Records {gdsrEntries.length}</span>
                          </div>
          
                          <div className="header-right">
                              <div className="search-refresh-wrapper">
                                  <button
                                      onClick={refreshData}
                                      className="refresh-button"
                                      disabled={isRefreshing}
                                      aria-label="Refresh data"
                                  >
                                      <FontAwesomeIcon
                                          icon={faSyncAlt}
                                          spin={isRefreshing}
                                          className={`refresh-icon ${isRefreshing ? 'spinning' : ''}`}
                                      />
                                  </button>
          
                                  <div className="search-bar">
                                      <FontAwesomeIcon icon={faSearch} className="search-icon" />
                                      <input
                                          type="text"
                                          placeholder="Search FIRs..."
                                          value={searchTerm}
                                          onChange={(e) => setSearchTerm(e.target.value)}
                                          className="search-input"
                                      />
                                      {searchTerm && (
                                          <button className="clear-search" onClick={() => setSearchTerm('')}>
                                              ×
                                          </button>
                                      )}
                                  </div>
                              </div>
                          </div>
                      </div>

            <div className="action-buttons">
                {/* <button
                    onClick={exportToExcel}
                    className="excel-btn"
                >
                    <FontAwesomeIcon icon={faFileExcel} /> ایکسل میں ایکسپورٹ کریں
                </button> */}
            </div>

            <div className="table-container">
                <div className="firtable-header" dir="rtl">
                    {/* <div className="header-cell">سیریل نمبر</div> */}
                    {/* <div className="header-cell">ٹوکن آئی ڈی</div> */}
                    <div className="header-cell">ٹوکن آئی ڈی</div>
                    {/* <div className="header-cell">بوک نمبر</div> */}
                    <div className="header-cell">تاریخ</div>
                    <div className="header-cell">نام و اطلاع دہندہ</div>
                    {/* <div className="header-cell">کیفیت جرم</div> */}
                    <div className="header-cell">تھانہ</div>
                    <div className="header-cell">ایکشن</div>
                </div>

                <div className="firtable-body">
                    {filteredEntries.length > 0 ? (
                        <AutoSizer>
                            {({ height, width }) => (
                                <List
                                    height={height}
                                    itemCount={filteredEntries.length}
                                    itemSize={45}
                                    width={width}
                                >
                                    {Row}
                                </List>
                            )}
                        </AutoSizer>
                    ) : (
                        <div className="empty-state">
                            <p>No entries found {searchTerm ? `matching "${searchTerm}"` : 'available'}</p>
                        </div>
                    )}
                </div>

                <div className="table-footer">
                    Showing {filteredEntries.length} of {gdsrEntries.length} entries
                </div>
            </div>
        </div>
    );
};

export default FormList;